import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { Pencil, Trash2, Plus, ChevronUp, ChevronDown, EyeOff } from 'lucide-react';
import { API, authHeaders, apiErr } from './adminApi';
import CategoryDialog from './PricingCategoryDialog';
import { newKey } from './PricingItemsEditor';

const emptyDraft = { id: null, category: '', description: '', items: [], visible: true };


const AdminPricing = ({ onUnauthorized }) => {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/pricing/all`, { headers: authHeaders() });
      setCats(data);
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized();
      else toast.error(`Failed to load: ${apiErr(err)}`);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onUnauthorized]); // API/apiErr/authHeaders are module-scoped imports and stable

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setDraft(emptyDraft); setDialogOpen(true); };
  const openEdit = (c) => setDraft({
    id: c.id,
    category: c.category,
    description: c.description || '',
    items: (c.items || []).map((it) => ({
      ...it,
      _key: newKey(),
      type: it.type || '',
      tag: it.tag || '',
      tag_color: it.tag_color || 'red',
    })),
    visible: c.visible !== false,
  }) || setDialogOpen(true);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        category: draft.category.trim(),
        description: draft.description,
        visible: draft.visible,
        items: draft.items
          .filter((it) => it.service.trim() && it.price.trim())
          .map((it) => ({
            service: it.service.trim(),
            price: it.price.trim(),
            type: it.type || null,
            tag: it.tag?.trim() || null,
            tag_color: it.tag?.trim() ? (it.tag_color || 'red') : null,
          })),
      };
      if (draft.id) {
        await axios.put(`${API}/api/pricing/${draft.id}`, payload, { headers: authHeaders() });
        toast.success('Pricing updated');
      } else {
        await axios.post(`${API}/api/pricing`, payload, { headers: authHeaders() });
        toast.success('Category created');
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(`Save failed: ${apiErr(err)}`);
    } finally {
      setSaving(false);
    }
  };

  const moveCategory = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= cats.length) return;
    const a = cats[idx]; const b = cats[j];
    try {
      await Promise.all([
        axios.put(`${API}/api/pricing/${a.id}`, { sort_order: b.sort_order }, { headers: authHeaders() }),
        axios.put(`${API}/api/pricing/${b.id}`, { sort_order: a.sort_order }, { headers: authHeaders() }),
      ]);
      await load();
    } catch (err) {
      toast.error(`Reorder failed: ${apiErr(err)}`);
    }
  };

  const toggleVisible = async (c) => {
    try {
      await axios.put(`${API}/api/pricing/${c.id}`, { visible: !c.visible }, { headers: authHeaders() });
      await load();
    } catch (err) {
      toast.error(`Toggle failed: ${apiErr(err)}`);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/api/pricing/${deleteTarget.id}`, { headers: authHeaders() });
      toast.success('Category deleted');
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(`Delete failed: ${apiErr(err)}`);
    }
  };

  return (
    <section data-testid="admin-pricing-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold">Pricing</h2>
          <p className="text-sm text-gray-400">{cats.length} categories · {cats.reduce((n, c) => n + (c.items?.length || 0), 0)} line items</p>
        </div>
        <Button data-testid="admin-new-category" onClick={openNew} className="bg-red-600 hover:bg-red-700 text-white">
          <Plus className="w-4 h-4 mr-2" /> New category
        </Button>
      </div>

      {loading ? (
        <p className="text-gray-400">Loading…</p>
      ) : (
        <div className="space-y-4" data-testid="admin-pricing-list">
          {cats.map((c, idx) => (
            <Card key={c.id} className="bg-slate-900 border-slate-800" data-testid={`pricing-cat-${c.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg text-white flex items-center gap-2">
                      {c.category}
                      {!c.visible && (
                        <span className="text-yellow-400 text-xs flex items-center gap-1">
                          <EyeOff className="w-3 h-3" /> Hidden
                        </span>
                      )}
                    </CardTitle>
                    {c.description && <p className="text-sm text-gray-400 mt-1 line-clamp-2">{c.description}</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="icon" variant="outline" data-testid={`pricing-up-${c.id}`} onClick={() => moveCategory(idx, -1)} disabled={idx === 0} className="border-slate-700 text-white hover:bg-slate-800 h-8 w-8">
                      <ChevronUp className="w-3 h-3" />
                    </Button>
                    <Button size="icon" variant="outline" data-testid={`pricing-down-${c.id}`} onClick={() => moveCategory(idx, 1)} disabled={idx === cats.length - 1} className="border-slate-700 text-white hover:bg-slate-800 h-8 w-8">
                      <ChevronDown className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="outline" data-testid={`pricing-edit-${c.id}`} onClick={() => openEdit(c)} className="border-slate-700 text-white hover:bg-slate-800">
                      <Pencil className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" data-testid={`pricing-toggle-${c.id}`} onClick={() => toggleVisible(c)} className="border-slate-700 text-white hover:bg-slate-800">
                      {c.visible ? 'Hide' : 'Show'}
                    </Button>
                    <Button size="icon" variant="outline" data-testid={`pricing-delete-${c.id}`} onClick={() => setDeleteTarget(c)} className="border-red-900/60 text-red-400 hover:bg-red-950/40 h-8 w-8">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="divide-y divide-slate-800">
                  {(c.items || []).map((it, i) => (
                    <div key={`${c.id}-${i}-${it.service}`} className="flex items-center justify-between py-2 text-sm">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-gray-300">{it.service}</span>
                        {it.type && (
                          <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${it.type === 'basic' ? 'bg-slate-700 text-gray-300' : 'bg-red-600/20 text-red-400'}`}>
                            {it.type}
                          </span>
                        )}
                        {it.tag && (
                          <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${it.tag_color === 'gold' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-600/25 text-red-400'}`}>
                            {it.tag}
                          </span>
                        )}
                      </div>
                      <span className="text-gray-200 font-medium">{it.price}</span>
                    </div>
                  ))}
                  {(c.items || []).length === 0 && (
                    <p className="text-xs text-gray-500 py-2">No line items.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <CategoryDialog open={dialogOpen} onOpenChange={setDialogOpen} draft={draft} setDraft={setDraft} onSave={save} saving={saving} />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-slate-900 border-slate-800 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this pricing category?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              &ldquo;{deleteTarget?.category}&rdquo; and all its line items will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="pricing-delete-confirm" onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};

export default AdminPricing;
